import { useMemo } from "react";
import { createFileRoute, useNavigate, useSearch, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { parseISO } from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppHeader } from "@/components/app/AppHeader";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  useAllProfiles,
  useAllSubmissions,
} from "@/hooks/use-admin-data";
import { DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";

const DEPTS = Object.keys(DEPARTMENT_CONFIGS) as Department[];

const searchSchema = z.object({
  dept: z.enum(DEPTS as [Department, ...Department[]]).default("editor"),
});

export const Route = createFileRoute("/_app/_admin/admin/departments")({
  validateSearch: (s) => searchSchema.parse(s),
  component: () => <Navigate to="/admin/settings" search={{ tab: "departments" } as never} />,
});

export function DepartmentsPage() {
  const search = useSearch({ strict: false }) as { dept?: Department };
  const dept: Department = (search.dept ?? "editor") as Department;
  const navigate = useNavigate();
  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useAllSubmissions();

  const now = new Date();
  const monthSubs = submissions.filter((s) => {
    const d = parseISO(s.submission_date);
    return (
      s.department === dept &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  });
  const deptPeople = profiles.filter((p) => p.department === dept);

  const data = useMemo(() => {
    return deptPeople
      .map((p) => {
        const subs = monthSubs.filter((s) => s.user_id === p.id);
        return {
          name: p.full_name.split(" ")[0],
          approved: subs.filter((s) => s.status === "approved").length,
          pending: subs.filter((s) => s.status === "pending").length,
          correction: subs.filter((s) => s.status === "correction_needed").length,
          rejected: subs.filter((s) => s.status === "rejected").length,
          total: subs.length,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [deptPeople, monthSubs]);

  return (
    <>
      <AppHeader subtitle="Per-department deep-dive" />
      <div className="px-[22px] pb-6">
        <Tabs
          value={dept}
          onValueChange={(v) => navigate({ to: ".", search: ((prev: Record<string, unknown>) => ({ ...prev, dept: v })) as never })}
        >
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
            {DEPTS.map((d) => (
              <TabsTrigger
                key={d}
                value={d}
                className="h-8 rounded-md border border-border bg-card px-3 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {DEPARTMENT_LABELS[d]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
              {DEPARTMENT_LABELS[dept as Department]}
            </div>
            <h2 className="text-[18px] font-bold">Output by user — this month</h2>
            <div className="mt-4 h-[300px]">
              {data.length === 0 ? (
                <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
                  No people in this department yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 11,
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                        background: "var(--card)",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="approved" stackId="a" fill="oklch(0.7 0.17 160)" />
                    <Bar dataKey="pending" stackId="a" fill="oklch(0.85 0.02 260)" />
                    <Bar dataKey="correction" stackId="a" fill="oklch(0.78 0.15 70)" />
                    <Bar dataKey="rejected" stackId="a" fill="oklch(0.62 0.22 27)" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
              Roster
            </div>
            <h2 className="text-[18px] font-bold">{deptPeople.length} people</h2>
            <div className="mt-3 flex flex-col gap-2">
              {deptPeople.length === 0 && (
                <p className="text-[12px] text-muted-foreground">No users.</p>
              )}
              {deptPeople.map((p) => {
                const subs = monthSubs.filter((s) => s.user_id === p.id);
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[12px] font-semibold">
                        {p.full_name}
                      </div>
                      {dept === "editor" && (
                        <div className="text-[10px] text-muted-foreground">
                          Target {p.monthly_target}/mo
                        </div>
                      )}
                    </div>
                    <span className="text-[12px] font-bold">{subs.length}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
